import React, { useEffect, useMemo, useRef, useState } from "react";
import { useChat, ChatMessage, ChatRoom, BookingOption, MessageType } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";
import { FaInbox, FaPaperPlane, FaCircle, FaSearch, FaUserCircle, FaLock, FaHeadset } from "react-icons/fa";
import { MediaBubble, AttachmentPicker, AudioRecorder, AttachmentPreview, uploadFileToS3 } from "../support/ChatMedia";

function baseUrl(): string {
    return (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");
}
function fmtTime(iso: string | null): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const now = new Date();
        if (d.toDateString() === now.toDateString()) {
            return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        }
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    } catch { return ""; }
}
function fmtFull(iso: string): string {
    try {
        return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
}

const SupportInbox: React.FC = () => {
    const { profile } = useAuth();
    const { socket, connected, role, superAdmin, myUid, rooms, refreshRooms } = useChat();
    const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState("");
    const [filter, setFilter] = useState("");
    const [otherTyping, setOtherTyping] = useState(false);
    const [pendingFile, setPendingFile] = useState<{ file: File; type: MessageType } | null>(null);
    const [uploading, setUploading] = useState(false);
    const [audioActive, setAudioActive] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const typingTimer = useRef<any>(null);
    const otherTypingTimer = useRef<any>(null);

    // Visible rooms: super admins see everything; regular staff see unassigned or their own.
    const visibleRooms = useMemo(() => {
        if (superAdmin) return rooms;
        return rooms.filter(
            (r) => !r.assignedStaff || r.assignedStaff === myUid,
        );
    }, [rooms, superAdmin, myUid]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const src = visibleRooms;
        if (!q) return src;
        return src.filter((r) =>
            (r.customerName || "").toLowerCase().includes(q) ||
            (r.customerEmail || "").toLowerCase().includes(q) ||
            (r.lastMessage || "").toLowerCase().includes(q),
        );
    }, [visibleRooms, filter]);

    // If the current conversation becomes unassigned-to-me mid-session, release it.
    useEffect(() => {
        if (!activeRoom) return;
        const stillVisible = visibleRooms.some((r) => r.id === activeRoom.id);
        if (!stillVisible) setActiveRoom(null);
    }, [visibleRooms, activeRoom]);


    // Load history on room change
    useEffect(() => {
        if (!activeRoom) return;
        let cancelled = false;
        (async () => {
            const res = await fetch(`${baseUrl()}/chat/rooms/${activeRoom.id}/messages`, { credentials: "include" });
            if (!res.ok) return;
            const data = await res.json();
            if (cancelled) return;
            setMessages(data.messages || []);
            socket?.emit("join_room", { roomId: activeRoom.id });
        })();
        return () => { cancelled = true; };
    }, [activeRoom?.id, socket]);

    // Keep activeRoom in a ref so the socket listener always sees the latest
    const activeRoomRef = useRef(activeRoom);
    activeRoomRef.current = activeRoom;

    // Socket event listeners
    useEffect(() => {
        if (!socket) return;
        const onNew = (msg: ChatMessage) => {
            const currentRoom = activeRoomRef.current;
            if (currentRoom && msg.roomId === currentRoom.id) {
                setMessages((prev) => {
                    // Dedup: skip if this message is already in the array
                    if (prev.some((m) => m.id === msg.id)) return prev;
                    return [...prev, msg];
                });
                if (msg.senderRole === "customer") {
                    socket.emit("mark_read", { roomId: msg.roomId });
                }
            }
            refreshRooms();
        };
        const onTyping = (p: { roomId: number; by: string; typing: boolean }) => {
            if (activeRoom && p.roomId === activeRoom.id && p.by !== "staff") {
                setOtherTyping(p.typing);
                if (p.typing) {
                    clearTimeout(otherTypingTimer.current);
                    otherTypingTimer.current = setTimeout(() => setOtherTyping(false), 3500);
                }
            }
        };
        socket.on("new_message", onNew);
        socket.on("typing", onTyping);
        return () => {
            socket.off("new_message", onNew);
            socket.off("typing", onTyping);
        };
    }, [socket, activeRoom?.id, refreshRooms]);

    // Autoscroll
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, otherTyping]);

    const sendMessage = () => {
        const body = draft.trim();
        if (!body || !socket || !activeRoom) return;
        socket.emit(
            "send_message",
            { roomId: activeRoom.id, body },
            (ack: any) => {
                if (ack?.ok) {
                    setDraft("");
                    // Optimistic UI update from ack (belt-and-suspenders with new_message event)
                    if (ack.message) {
                        setMessages((prev) => {
                            if (prev.some((m) => m.id === ack.message.id)) return prev;
                            return [...prev, ack.message];
                        });
                    }
                }
            },
        );
    };

    const sendAttachment = async (file: File, messageType: MessageType) => {
        if (!socket || !activeRoom) {
            setPendingFile(null);
            alert("Not connected. Please wait and try again.");
            return;
        }
        setUploading(true);
        try {
            const { key } = await uploadFileToS3({
                file,
                roomId: activeRoom.id,
                messageType,
            });

            socket.emit(
                "send_message",
                {
                    roomId: activeRoom.id,
                    body: "",
                    messageType,
                    attachmentKey: key,
                    attachmentName: file.name,
                    attachmentSize: file.size,
                    attachmentMime: file.type,
                },
                (ack: any) => {
                    if (ack?.ok && ack.message) {
                        // Optimistic UI update from ack (belt-and-suspenders with new_message event)
                        setMessages((prev) => {
                            if (prev.some((m) => m.id === ack.message.id)) return prev;
                            return [...prev, ack.message];
                        });
                    } else if (!ack?.ok) {
                        console.warn("[inbox] attachment send failed:", ack?.error);
                    }
                },
            );
        } catch (err: any) {
            console.error("[inbox] upload failed:", err);
            alert(err.message || "Failed to upload file");
        } finally {
            setUploading(false);
            setPendingFile(null);
        }
    };

    const handleFilePicked = (file: File, type: MessageType) => {
        setPendingFile({ file, type });
    };

    const handleAudioRecorded = (blob: Blob) => {
        // Normalize MIME: MediaRecorder may produce "audio/webm;codecs=opus"
        // but the server only accepts the base MIME (e.g. "audio/webm").
        const mime = (blob.type || "audio/webm").split(";")[0];
        const ext = mime.endsWith("mp4") ? "m4a" : mime.endsWith("webm") ? "webm" : "ogg";
        const file = new File([blob], `voice-message.${ext}`, { type: mime });
        sendAttachment(file, "audio");
    };

    const handleSendPendingFile = () => {
        if (!pendingFile) return;
        sendAttachment(pendingFile.file, pendingFile.type);
    };

    const handleChange = (v: string) => {
        setDraft(v);
        if (!activeRoom || !socket) return;
        socket.emit("typing", { roomId: activeRoom.id, typing: true });
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => {
            socket.emit("typing", { roomId: activeRoom.id, typing: false });
        }, 1200);
    };

    const takeOver = async () => {
        if (!activeRoom) return;
        try {
            const res = await fetch(`${baseUrl()}/chat/rooms/${activeRoom.id}/escalate`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason: "Staff takeover" }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                console.error("[inbox] takeover failed:", data.error);
            }
        } catch (err) {
            console.error("[inbox] failed to takeover:", err);
        }
    };

    if (role !== "staff") {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <div className="text-gray-500 text-lg font-semibold mb-1">Staff only</div>
                    <div className="text-gray-400 text-sm">You need an internal-staff role to access the support inbox.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-screen-xl mx-auto px-4 py-4 h-[calc(100vh-80px)] flex flex-col">
            <div className="flex items-center gap-3 mb-3 shrink-0">
                <FaInbox className="text-primary text-2xl" />
                <h1 className="text-2xl font-bold text-gray-900">Support Inbox</h1>
                <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
                    <FaCircle size={7} className={connected ? "text-green-500" : "text-gray-300"} />
                    {connected ? "Connected" : "Disconnected"}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex-1 min-h-0">
                {/* Room list */}
                <aside className="border-r border-gray-100 flex flex-col min-h-0">
                    <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                            <input
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Search customers…"
                                className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-primary/30"
                            />
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {filtered.length === 0 && (
                            <div className="p-6 text-center text-gray-400 text-sm">No conversations yet.</div>
                        )}
                        {filtered.map((r) => {
                            const active = r.id === activeRoom?.id;
                            const unread = r.unreadForStaff || 0;
                            return (
                                <button
                                    key={r.id}
                                    onClick={() => setActiveRoom(r)}
                                    className={`w-full text-left px-3 py-3 border-b border-gray-50 flex gap-3 items-start transition ${
                                        active ? "bg-primary/5" : "hover:bg-gray-50"
                                    }`}
                                >
                                    <FaUserCircle className="text-gray-400 text-3xl shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-semibold text-sm text-gray-900 truncate">
                                                {r.customerName || "Customer"}
                                            </span>
                                            <span className="text-[10px] text-gray-400 shrink-0">
                                                {fmtTime(r.lastMessageAt)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500 truncate">
                                            {r.lastMessage || "No messages yet"}
                                        </div>
                                        <div className="flex items-center justify-between mt-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                                    r.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                                                }`}>
                                                    {r.status}
                                                </span>
                                                {r.escalated && (
                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                                        Escalated
                                                    </span>
                                                )}
                                                {r.aiMode && !r.escalated && r.status === "open" && (
                                                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600">
                                                        AI
                                                    </span>
                                                )}
                                            </div>
                                            {unread > 0 && (
                                                <span className="min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                                                    {unread > 9 ? "9+" : unread}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* Conversation pane */}
                <section className="flex flex-col min-h-0">
                    {!activeRoom ? (
                        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                            Select a conversation to start responding.
                        </div>
                    ) : (
                        <>
                            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
                                <div>
                                    <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                                        {activeRoom.customerName || "Customer"}
                                        {activeRoom.assignedStaff === myUid && (
                                            <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full uppercase">
                                                Assigned to you
                                            </span>
                                        )}
                                        {activeRoom.escalated && (
                                            <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase">
                                                Escalated
                                            </span>
                                        )}
                                        {activeRoom.aiMode && !activeRoom.escalated && (
                                            <span className="text-[10px] font-medium bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full uppercase">
                                                AI Active
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {activeRoom.customerEmail || "—"}
                                        {activeRoom.escalationReason && activeRoom.escalated && (
                                            <span className="ml-2 text-amber-600"> — {activeRoom.escalationReason}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                                    {activeRoom.aiMode && !activeRoom.escalated ? (
                                        <button
                                            onClick={takeOver}
                                            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition text-[11px] font-semibold"
                                            title="Take over from AI and chat directly"
                                        >
                                            <FaHeadset size={10} />
                                            Take Over
                                        </button>
                                    ) : (
                                        <>
                                            <FaLock size={9} />
                                            Only the customer can end this chat
                                        </>
                                    )}
                                </div>
                            </div>

                            <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0 px-4 py-3 bg-[#F7F9FC] space-y-2">
                                {messages.length === 0 && (
                                    <div className="text-center text-xs text-gray-400 py-8">
                                        No messages yet — say hello!
                                    </div>
                                )}
                                {messages.map((m) => {
                                    const mine = m.senderRole === "staff";
                                    const isBot = m.senderRole === "bot";
                                    const isMedia = m.messageType && m.messageType !== "text";
                                    return (
                                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                                                mine
                                                    ? "bg-primary text-white rounded-br-sm"
                                                    : isBot
                                                        ? "bg-indigo-50 text-gray-800 border border-indigo-200 rounded-bl-sm"
                                                        : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm"
                                            }`}>
                                                {isBot && (
                                                    <div className="text-[10px] font-bold text-indigo-600 mb-0.5 flex items-center gap-1">
                                                        <span className="w-3 h-3 rounded-full bg-indigo-100 flex items-center justify-center text-[7px]">AI</span>
                                                        {m.senderName || "Assistant"}
                                                    </div>
                                                )}
                                                {!mine && !isBot && m.senderName && (
                                                    <div className="text-[10px] font-bold text-gray-500 mb-0.5">{m.senderName}</div>
                                                )}
                                                {/* Media content */}
                                                {isMedia ? (
                                                    <MediaBubble message={m} mine={mine} />
                                                ) : (
                                                    m.body
                                                )}
                                                {/* Caption for media with text */}
                                                {isMedia && m.body && (
                                                    <div className="mt-1">{m.body}</div>
                                                )}
                                                {(isBot && m.metadata?.options?.length) && (
                                                    <div className="mt-2 space-y-1.5">
                                                        {m.metadata!.options!.map((opt: BookingOption, i: number) => (
                                                            <div
                                                                key={i}
                                                                className="px-3 py-2 rounded-xl bg-white border border-indigo-200 text-xs"
                                                            >
                                                                <div className="font-semibold text-gray-800">{opt.label}</div>
                                                                <div className="text-[10px] text-gray-400">
                                                                    PNR {opt.pnr} · {opt.status}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className={`text-[9px] mt-1 ${mine ? "text-white/70" : "text-gray-400"}`}>
                                                    {fmtFull(m.createdAt)}
                                                    {mine && m.readAt && <span className="ml-1">· Read</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {otherTyping && (
                                    <div className="flex justify-start">
                                        <div className="bg-white border border-gray-200 px-3 py-2 rounded-2xl text-xs text-gray-500">
                                            <span className="inline-flex gap-1">
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-.15s]" />
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-.3s]" />
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Attachment preview */}
                            {pendingFile && (
                                <AttachmentPreview
                                    file={pendingFile.file}
                                    type={pendingFile.type}
                                    uploading={uploading}
                                    onCancel={() => setPendingFile(null)}
                                />
                            )}

                            {activeRoom.status === "open" ? (
                                pendingFile && !uploading ? (
                                    <div className="border-t border-gray-100 px-3 py-3 flex gap-2 bg-white shrink-0">
                                        <input
                                            value={draft}
                                            onChange={(e) => setDraft(e.target.value)}
                                            placeholder="Add a caption…"
                                            className="flex-1 px-3 py-2 rounded-full bg-gray-100 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-primary/40"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSendPendingFile}
                                            className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-blue-900 transition"
                                            aria-label="Send attachment"
                                        >
                                            <FaPaperPlane size={13} />
                                        </button>
                                    </div>
                                ) : (
                                    <form
                                        onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                                        className="border-t border-gray-100 px-3 py-3 flex gap-2 bg-white shrink-0 items-center"
                                    >
                                        {!audioActive && (
                                            <AttachmentPicker
                                                onFilePicked={handleFilePicked}
                                                disabled={!connected || uploading}
                                            />
                                        )}
                                        {audioActive ? (
                                            <AudioRecorder
                                                onRecorded={handleAudioRecorded}
                                                disabled={!connected || uploading}
                                                onRecordingChange={setAudioActive}
                                            />
                                        ) : (
                                            <>
                                                <input
                                                    value={draft}
                                                    onChange={(e) => handleChange(e.target.value)}
                                                    placeholder={`Reply as ${profile?.role || "staff"}…`}
                                                    className="flex-1 px-3 py-2 rounded-full bg-gray-100 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-primary/40"
                                                    disabled={!connected || uploading}
                                                />
                                                {draft.trim() ? (
                                                    <button
                                                        type="submit"
                                                        disabled={!draft.trim() || !connected}
                                                        className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-50 hover:bg-blue-900 transition"
                                                        aria-label="Send"
                                                    >
                                                        <FaPaperPlane size={13} />
                                                    </button>
                                                ) : (
                                                    <AudioRecorder
                                                        onRecorded={handleAudioRecorded}
                                                        disabled={!connected || uploading}
                                                        onRecordingChange={setAudioActive}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </form>
                                )
                            ) : (
                                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 text-xs text-gray-500 text-center shrink-0">
                                    This conversation was closed by the customer.
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

export default SupportInbox;
